import { StayPurposeData } from '../types/security';

/**
 * Input type for purpose data before percentage calculation.
 */
export interface StayPurposeInput {
  purpose: string;
  count: number;
}

/**
 * Calculates the percentage of each stay purpose relative to the total.
 * Returns an empty array if the input is empty.
 * Percentages are rounded to 1 decimal place and adjusted so the sum equals 100%.
 *
 * @param purposes - Array of stay purpose counts
 * @returns Array of StayPurposeData with calculated percentages
 */
export function calculatePurposePercentages(purposes: StayPurposeInput[]): StayPurposeData[] {
  if (purposes.length === 0) {
    return [];
  }

  const total = purposes.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    return purposes.map(p => ({ purpose: p.purpose, count: p.count, percentage: 0 }));
  }

  // Calculate raw percentages rounded to 1 decimal
  const results: StayPurposeData[] = purposes.map(p => ({
    purpose: p.purpose,
    count: p.count,
    percentage: Math.round((p.count / total) * 1000) / 10,
  }));

  // Adjust rounding so the sum is exactly 100%
  const sumPercentages = results.reduce((s, r) => s + Math.round(r.percentage * 10), 0);
  const diff = 1000 - sumPercentages; // difference in tenths of a percent

  if (diff !== 0) {
    // Find the item with the largest remainder to adjust
    const remainders = purposes.map((p, i) => ({
      index: i,
      remainder: ((p.count / total) * 1000) - Math.floor((p.count / total) * 1000),
    }));
    remainders.sort((a, b) => b.remainder - a.remainder);

    const steps = Math.abs(diff);
    const direction = diff > 0 ? 1 : -1;
    for (let i = 0; i < steps && i < remainders.length; i++) {
      results[remainders[i].index].percentage =
        Math.round((results[remainders[i].index].percentage + direction * 0.1) * 10) / 10;
    }
  }

  return results;
}
